import { useEffect, useState } from 'react';

import { useDebounce } from './useDebounce';

interface positionProps {
  style: {
    style: {
      inset: string;
    };
  };
  status: boolean;
  updatePosition: () => void;
}

interface optionProps {
  mouseEvent: 'click' | 'hover';
  position: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  offset: number;
  maxFixedHeight?: `${number}${'px' | 'rem'}`;
  horizontalPosition?: 'left' | 'right';
  mobileBreakpoint?: `${number}${'px' | 'rem'}`;
  fullWidth: boolean;
}

const BODY_PADDING = 10;
const MIN_HEIGHT = 90;
const MIN_WIDTH = 300;
const DEBOUNCE_DELAY = 0;
const MOBILE_BREAKPOINT = 768;
const HALF = 2;

export function usePositionedFloaters<T extends HTMLElement = HTMLElement>(
  positionedElement: T | null,
  referenceElement: T | null,
  options: optionProps
): positionProps {
  const [status, setStatus] = useState(false);
  const defaultStyle = {
    inset: '0 auto auto 0',
    opacity: 0,
    maxHeight: '100%',
    maxWidth: '100%',
    minWidth: MIN_WIDTH,
    overflowX: 'hidden',
    overflowY: 'auto',
    margin: '0rem',
  };

  const [style, setStyle] = useState(defaultStyle);

  const sizeToPx = (size: `${number}${'px' | 'rem'}`): number => {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return size.indexOf('rem') !== -1 ? parseFloat(size) * rootFontSize : parseFloat(size);
  };

  const setHeight = (refRect: DOMRect, pos: string): number => {
    if (!refRect) return 0;

    const _maxHeight =
      pos === 'bottom'
        ? window.innerHeight - refRect.bottom - options.offset - BODY_PADDING
        : refRect.top - options.offset - BODY_PADDING;

    return !options.maxFixedHeight || sizeToPx(options.maxFixedHeight) > _maxHeight
      ? _maxHeight
      : sizeToPx(options.maxFixedHeight);
  };

  const setWidth = (refRect: DOMRect, pos: string): number => {
    if (!refRect) return 0;

    const _availableWidth =
      pos === 'left' ? window.innerWidth - refRect.left - BODY_PADDING : refRect.right - BODY_PADDING;

    return _availableWidth <= MIN_WIDTH ? MIN_WIDTH : _availableWidth;
  };

  const setLeft = (refRect: DOMRect, posRect: DOMRect, isMobile: boolean) => {
    if (isMobile) {
      return `${refRect.left - Math.abs(posRect.width - refRect.width) / HALF}px`;
    } else {
      return `${refRect.left}px`;
    }
  };

  const calcPosition = (): typeof defaultStyle => {
    const positionedRect = (positionedElement as HTMLElement | null)?.getBoundingClientRect();
    const referenceRect = (referenceElement as HTMLElement | null)?.getBoundingClientRect();
    let yPos = 'bottom';
    let xPos = 'left';

    const isMobile = window.matchMedia(
      `(max-width: ${options.mobileBreakpoint ? sizeToPx(options.mobileBreakpoint) : MOBILE_BREAKPOINT}px)`
    ).matches;

    if (!referenceRect || !positionedRect) {
      return defaultStyle;
    }

    if (window.innerHeight - referenceRect.bottom - BODY_PADDING < MIN_HEIGHT) {
      yPos = 'top';
    }

    if (
      (window.innerWidth - referenceRect.left - BODY_PADDING < MIN_WIDTH || options.horizontalPosition === 'right') &&
      !isMobile
    ) {
      xPos = 'right';
    }

    return {
      inset: `${yPos === 'bottom' ? referenceRect[yPos] + 'px' : 'auto'}
              ${xPos === 'right' ? window.innerWidth - referenceRect[xPos] + 'px' : 'auto'}
              ${yPos === 'top' ? window.innerHeight - referenceRect[yPos] + 'px' : 'auto'}
              ${xPos === 'left' ? setLeft(referenceRect, positionedRect, isMobile) : 'auto'}`,
      maxHeight: `${setHeight(referenceRect, yPos)}px`,
      maxWidth: `${setWidth(referenceRect, xPos)}px`,
      minWidth: options.fullWidth ? referenceRect.width : MIN_WIDTH,
      overflowX: 'hidden',
      overflowY: 'auto',
      opacity: 1,
      margin: `${options.offset}px 0`,
    };
  };

  const handleMouseEvent = (): void => {
    setStyle({
      ...style,
      ...calcPosition(),
    });
    setStatus(true);
  };

  const updatePosition = (): void => {
    if (status) {
      setTimeout(() => {
        setStyle((prevStyle) => ({
          ...prevStyle,
          ...calcPosition(),
        }));
      }, 0);
    }
  };

  const handleClickOutside = (evt: Event): void => {
    if (
      !referenceElement ||
      (referenceElement as unknown as HTMLElement).contains(evt.target as Node) ||
      !positionedElement ||
      (positionedElement as unknown as HTMLElement).contains(evt.target as Node)
    ) {
      return;
    }
  };

  const debouncedUpdatePosition = useDebounce(updatePosition, DEBOUNCE_DELAY);

  useEffect(() => {
    if (referenceElement !== null) {
      if (options.mouseEvent === 'click') {
        // eslint-disable-next-line no-extra-semi
        (referenceElement as unknown as HTMLElement).addEventListener('click', handleMouseEvent);
      } else {
        // eslint-disable-next-line no-extra-semi
        (referenceElement as unknown as HTMLElement).addEventListener('mouseenter', handleMouseEvent);
        // eslint-disable-next-line no-extra-semi
        (referenceElement as unknown as HTMLElement).addEventListener('mouseleave', handleClickOutside);
      }
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      window.addEventListener('resize', debouncedUpdatePosition);
    }

    return () => {
      if (referenceElement !== null) {
        if (options.mouseEvent === 'click') {
          // eslint-disable-next-line no-extra-semi
          (referenceElement as unknown as HTMLElement).removeEventListener('click', handleMouseEvent);
        } else {
          // eslint-disable-next-line no-extra-semi
          (referenceElement as unknown as HTMLElement).removeEventListener('mouseenter', handleMouseEvent);
          // eslint-disable-next-line no-extra-semi
          (referenceElement as unknown as HTMLElement).removeEventListener('mouseleave', handleClickOutside);
        }
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        window.removeEventListener('resize', debouncedUpdatePosition);
      }
    };
  });

  return {
    style: {
      style,
    },
    status,
    updatePosition,
  };
}
